import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { projects } from '../data/mock';
import DeviceMockup from './DeviceMockup';
import { localizedPath, sectionId } from '../utils/routes';
import { usePageTransition } from './PageTransition';

// 3D Tilt Card Component - Works on both desktop and mobile
const TiltCard = ({ children, className, onClick }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });
  const [isActive, setIsActive] = useState(false);

  const calculateTilt = (clientX, clientY) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const posX = clientX - centerX;
    const posY = clientY - centerY;
    
    // Calculate rotation (max 8 degrees for subtle effect)
    const rotateY = (posX / (rect.width / 2)) * 8;
    const rotateX = -(posY / (rect.height / 2)) * 8;
    
    setTilt({ rotateX, rotateY });
  };

  // Mouse events (desktop)
  const handleMouseMove = (e) => calculateTilt(e.clientX, e.clientY);
  const handleMouseEnter = () => setIsActive(true);
  const handleMouseLeave = () => {
    setIsActive(false);
    setTilt({ rotateX: 0, rotateY: 0 });
  };

  // Touch events (mobile)
  const handleTouchStart = () => setIsActive(true);
  const handleTouchMove = (e) => {
    if (e.touches.length > 0) {
      calculateTilt(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  const handleTouchEnd = () => {
    setIsActive(false);
    setTilt({ rotateX: 0, rotateY: 0 });
  };

  return (
    <div
      ref={cardRef}
      className={className}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) scale(${isActive ? 1.02 : 1})`,
        transition: isActive ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Shine effect overlay */}
      <div 
        className="absolute inset-0 pointer-events-none rounded-2xl z-10"
        style={{
          background: isActive 
            ? `radial-gradient(circle at ${50 + tilt.rotateY * 3}% ${50 - tilt.rotateX * 3}%, rgba(255,215,0,0.15) 0%, transparent 60%)`
            : 'none',
          transition: 'background 0.1s ease-out',
        }}
      />
      {children}
    </div>
  );
};

const PortfolioSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'it';
  const { transitionTo } = usePageTransition();
  const containerRef = useRef(null);
  const scrollGalleryRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Desktop: sync vertical scroll to horizontal gallery scroll (parallax effect)
  // Disabled permanently once the user interacts with the gallery (drag, wheel, trackpad)
  const parallaxDisabledRef = useRef(false);
  const parallaxWritingRef = useRef(false);
  
  useEffect(() => {
    if (isMobile) return;
    const gallery = scrollGalleryRef.current;
    if (!gallery) return;
    
    const unsubscribe = scrollYProgress.on('change', (v) => {
      if (parallaxDisabledRef.current || isDraggingRef.current) return;
      const adjusted = Math.max(0, (v - 0.3) / 0.5);
      const maxScroll = gallery.scrollWidth - gallery.clientWidth;
      parallaxWritingRef.current = true;
      gallery.scrollLeft = adjusted * maxScroll * 0.5;
      requestAnimationFrame(() => { parallaxWritingRef.current = false; });
    });
    
    return () => unsubscribe();
  }, [isMobile, scrollYProgress]);

  // Detect ANY user interaction with the gallery and kill parallax
  useEffect(() => {
    if (isMobile) return;
    const gallery = scrollGalleryRef.current;
    if (!gallery) return;

    // Wheel/trackpad on the gallery
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) > 2) {
        parallaxDisabledRef.current = true;
      }
    };

    // Native scroll not caused by parallax
    const onScroll = () => {
      if (!parallaxWritingRef.current && !isDraggingRef.current) {
        parallaxDisabledRef.current = true;
      }
    };

    gallery.addEventListener('wheel', onWheel, { passive: true });
    gallery.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      gallery.removeEventListener('wheel', onWheel);
      gallery.removeEventListener('scroll', onScroll);
    };
  }, [isMobile]);

  // Desktop: mouse drag to scroll
  useEffect(() => {
    if (isMobile) return;
    const gallery = scrollGalleryRef.current;
    if (!gallery) return;
    
    const onMouseDown = (e) => {
      if (e.target.closest('a, button')) return;
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      dragStartRef.current = { x: e.pageX, scrollLeft: gallery.scrollLeft };
      gallery.style.cursor = 'grabbing';
      e.preventDefault();
    };
    
    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const dx = e.pageX - dragStartRef.current.x;
      if (Math.abs(dx) > 5) hasDraggedRef.current = true;
      gallery.scrollLeft = dragStartRef.current.scrollLeft - dx;
    };
    
    const onMouseUp = () => {
      if (isDraggingRef.current && hasDraggedRef.current) {
        parallaxDisabledRef.current = true;
      }
      isDraggingRef.current = false;
      gallery.style.cursor = 'grab';
    };
    
    gallery.style.cursor = 'grab';
    gallery.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    
    return () => {
      gallery.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isMobile]);

  const handleProjectClick = (project) => {
    if (hasDraggedRef.current) return;
    if (project.isRealProject && project.slug) {
      transitionTo(localizedPath(currentLang, 'project', { slug: project.slug }));
    }
  };

  return (
    <section id={sectionId(currentLang, 'portfolio')} ref={containerRef} className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
              {t('portfolio.subtitle')}
            </span>
            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
              {t('portfolio.title')}
              <span className="text-outline">{t('portfolio.title_outline')}</span>
            </h2>
          </motion.div>
          
          <motion.p
            className="text-white/80 text-lg font-light max-w-md"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('portfolio.description')}
          </motion.p>
        </div>
      </div>

      {/* Horizontal scroll gallery - scrollable on all devices, parallax + drag on desktop */}
      <div ref={scrollGalleryRef} className="overflow-x-auto scrollbar-hide">
        <div 
          className="flex gap-6 md:gap-8 pl-4 md:pl-12 lg:pl-16 pr-4 md:pr-6 lg:pr-12"
        >
        {projects.map((project, index) => {
          // Get translated project data with fallback to mock data
          const tp = (field) => {
            const key = `case_study.projects.${project.id}.${field}`;
            const translated = t(key, { returnObjects: true, defaultValue: null });
            if (translated && translated !== key) return translated;
            return project[field];
          };

          return (
          <motion.div
            key={project.id}
            className={`flex-shrink-0 w-[92vw] md:w-[60vw] lg:w-[50vw] group ${project.isRealProject ? 'cursor-pointer' : ''}`}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: index * 0.15 }}
            onClick={() => handleProjectClick(project)}
          >
            {/* Card container with 3D tilt effect */}
            <TiltCard className="relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden mb-6 group-hover:border-[#FFD700]/30 transition-colors duration-500">
              
              {/* Top bar with badges */}
              <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                  {/* Project logo or number */}
                  {project.clientLogoMark ? (
                    <img
                      src={project.clientLogoMark}
                      alt={`${project.title} logo`}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <span className="font-display text-2xl text-[#FFD700]/50">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  )}
                  {/* Location if available */}
                  {project.location && (
                    <div className="flex items-center gap-1.5 text-white/40">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <span className="text-[10px] tracking-wider uppercase">{tp('location')}</span>
                    </div>
                  )}
                </div>
                
                {/* Real Project Badge */}
                {project.isRealProject && (
                  <span className="bg-[#FFD700] text-black text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 font-bold rounded-full">
                    {t('case_study.real_project')}
                  </span>
                )}
              </div>

              {/* Device Mockups or Image */}
              <div className="relative p-4 md:p-8 bg-gradient-to-b from-transparent to-black/20">
                {project.desktopImage && project.mobileImage ? (
                  <DeviceMockup 
                    desktopImage={project.desktopImage}
                    mobileImage={project.mobileImage}
                    projectTitle={project.title}
                  />
                ) : (
                  <div className="relative aspect-video overflow-hidden rounded-lg">
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.7 }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all duration-500" />
                  </div>
                )}
              </div>

              {/* Visit site button */}
              {project.link && (
                <div className="px-4 md:px-6 pb-4 md:pb-6">
                  <motion.a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 border border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700] rounded-lg hover:bg-[#FFD700]/20 transition-all duration-300"
                    onClick={(e) => e.stopPropagation()}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="text-xs tracking-[0.2em] uppercase font-medium">{t('case_study.visit_site')}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </motion.a>
                </div>
              )}
            </TiltCard>

            {/* Project info */}
            <div className="space-y-3 px-2">
              <div className="flex items-center justify-between">
                <span className="text-[#FFD700] text-xs tracking-[0.3em] uppercase">
                  {project.category}
                </span>
                {project.year && (
                  <span className="text-white/30 text-sm">
                    {project.year}
                  </span>
                )}
              </div>
              
              <h3 className="font-display text-2xl md:text-3xl text-white group-hover:text-[#FFD700] transition-colors duration-500">
                {tp('title')}
              </h3>
              
              <p className="text-white/40 font-light">
                {tp('description')}
              </p>

              {/* Services tags for real projects */}
              {project.services && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {(tp('services') || []).map((service) => (
                    <span 
                      key={service}
                      className="text-[10px] tracking-[0.15em] uppercase text-white/30 border border-white/10 px-2 py-1"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )})}

        {/* View all CTA - DISABLED: Re-enable when 5+ real projects are available */}
        {/*
        <motion.div
          className="hidden md:flex flex-shrink-0 w-[40vw] items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <motion.button
              className="elegant-button px-12 py-5 text-xs tracking-[0.3em] uppercase mb-4"
              whileHover={{ scale: 1.02 }}
            >
              {t('portfolio.view_all')}
            </motion.button>
            <p className="text-white/30 text-sm">{t('portfolio.more_projects')}</p>
          </div>
        </motion.div>
        */}
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        className="flex flex-col items-center gap-6 mt-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-px bg-white/20" />
          <span className="text-white/30 text-xs tracking-[0.3em] uppercase">{t('portfolio.scroll_hint')}</span>
          <div className="w-16 h-px bg-white/20" />
        </div>
        
        <motion.button
          onClick={() => transitionTo(localizedPath(currentLang, 'portfolio'))}
          className="group flex items-center gap-3 px-8 py-3 border border-white/10 hover:border-[#FFD700]/40 transition-all duration-500"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="portfolio-view-all-btn"
        >
          <span className="text-white/50 group-hover:text-[#FFD700] text-xs tracking-[0.25em] uppercase transition-colors duration-500">
            {t('portfolio.view_all')}
          </span>
          <svg className="w-4 h-4 text-white/30 group-hover:text-[#FFD700] group-hover:translate-x-1 transition-all duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default PortfolioSection;
